// src/lib/designStudio.js
//
// Pure helpers for the Design Studio (multi-model visual development takes).
// The provider catalog here must stay in step with IMAGE_PROVIDERS in
// worker/src/lib/image-models.js — the worker is the source of truth for
// which env key unlocks which provider; this list is what the composer
// offers. Midjourney is deliberately absent (no official API; the unofficial
// wrappers violate their ToS).

export const DESIGN_PROVIDERS = [
  { id: 'openai',   label: 'GPT Image',            badge: 'GPT',  note: 'best at following notes' },
  { id: 'gemini',   label: 'Gemini (Nano Banana)', badge: 'GEM',  note: 'strong edits & consistency' },
  { id: 'flux',     label: 'Flux',                 badge: 'FLUX', note: 'technical quality' },
  { id: 'seedream', label: 'Seedream',             badge: 'SEED', note: 'stylized — feeds Seedance for video' },
  // XAI_API_KEY landed on the worker 2026-08-14 — Grok is a full participant.
  { id: 'grok',     label: 'Grok Imagine',         badge: 'GROK', note: 'stylized templates, cheap' },
]

// Model provenance is deliberately demoted in the workspace: the production
// cares about the design, not who rendered it. Tiny badge, not a headline.
export function providerBadge(id) {
  return DESIGN_PROVIDERS.find(p => p.id === id)?.badge || (id || '?').slice(0, 4).toUpperCase()
}

// The decision ladder — status, not folders. `is_reference` (the star) stays
// a separate lightweight signal: "use this to steer", not a rung.
export const TAKE_STATUSES = {
  exploration: { label: 'Exploration', chip: 'bg-warm-50 text-warm-500 border-warm-200' },
  candidate:   { label: 'Candidate',   chip: 'bg-navy-50 text-navy-600 border-navy-200' },
  approved:    { label: 'Approved',    chip: 'bg-green-50 text-green-700 border-green-200' },
  canon:       { label: 'Canon',       chip: 'bg-amber-50 text-amber-700 border-amber-300' },
}
export const TAKE_STATUS_ORDER = ['exploration', 'candidate', 'approved', 'canon']

// One brief → one prompt, shared verbatim across every model so the grid is a
// fair comparison. The caller may hand-edit the composed prompt before queueing.
export function composeTakePrompt({ propertyTitle, subjectName, briefText, feedbackContext, canonNotes = [] }) {
  const parts = []
  const who = subjectName?.trim()
  const from = propertyTitle?.trim()
  parts.push(`Character design reference${who ? ` for "${who}"` : ''}${from ? ` from "${from}"` : ''}.`)
  if (briefText?.trim()) parts.push(briefText.trim())
  if (feedbackContext?.trim()) parts.push(`Address these notes: ${feedbackContext.trim()}`)
  const canon = canonNotes.map(n => n?.trim()).filter(Boolean)
  if (canon.length) {
    parts.push(`Established canon for this ${who ? 'character' : 'property'} — MUST PRESERVE:\n${canon.map(n => `- ${n}`).join('\n')}`)
  }
  parts.push('Full-body character concept art on a clean neutral background, model-sheet quality, no text or watermarks.')
  return parts.join('\n\n')
}

export const TAKE_STATUS_LABEL = {
  queued: 'Queued',
  generating: 'Generating…',
  complete: 'Ready',
  failed: 'Failed',
}

// Claude's prompt-fit verdict on a finished take (design_takes.assessment.verdict).
export const ASSESSMENT_VERDICTS = {
  meets:   { label: 'Meets brief', chip: 'bg-green-50 text-green-700 border-green-200' },
  partial: { label: 'Partial',     chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  misses:  { label: 'Off brief',   chip: 'bg-red-50 text-red-600 border-red-200' },
}

// "Agree with Claude's notes" → the redraft prompt: the take's original
// prompt plus an explicit revision pass built from the human comments (which
// take precedence) and the assessment's missed points. Pure — the caller
// queues it as a new take with refined_from set.
export function composeRedraftPrompt(take) {
  const notes = []
  for (const c of take.comments || []) {
    if (c.body?.trim()) notes.push(`- ${c.body.trim()} [team note — highest priority]`)
  }
  for (const m of take.assessment?.missed || []) notes.push(`- ${m}`)
  if (!notes.length && take.assessment?.summary) notes.push(`- ${take.assessment.summary}`)
  return [
    String(take.prompt || '').trim(),
    `REVISION PASS — a previous image generated from this exact brief fell short. Keep everything the brief asks for (including every avoid-instruction), and this time correct the following:\n${notes.join('\n')}`,
  ].join('\n\n')
}

export function briefHasActiveTakes(brief) {
  return (brief?.takes || []).some(t => t.status === 'queued' || t.status === 'generating')
}

// True while the worker owes this brief anything — generation, an assessment,
// or a refinement pass. Drives the hook's polling.
export function briefHasActivity(brief) {
  if (briefHasActiveTakes(brief)) return true
  if ((brief?.takes || []).some(t => t.assessment_status === 'pending' || t.assessment_status === 'assessing')) return true
  return (brief?.refinements || []).some(r => r.status === 'queued' || r.status === 'processing')
}

// ─── Take numbers: one vocabulary for Aaron, the page and Janet ─────────────
// "Refine from 1 and 5" has to mean the same takes on the brief page and in
// Janet's chat. The gallery re-sorts by decision status, so the number is NOT
// the display position: it is the take's place in the order it was queued
// (created_at, then id). A deleted take closes the gap — numbers are stable
// for as long as the takes are.
export function numberTakes(takes = []) {
  const sorted = [...takes].sort((a, b) =>
    String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.id).localeCompare(String(b.id)))
  const map = new Map()
  sorted.forEach((t, i) => map.set(t.id, i + 1))
  return map
}

export function takeByNumber(takes = [], number) {
  const n = Number(number)
  if (!Number.isInteger(n) || n < 1) return null
  const map = numberTakes(takes)
  return takes.find(t => map.get(t.id) === n) || null
}

// ─── What Janet may propose in the Design Studio ────────────────────────────
// Janet proposes; a human click runs it (the card in her chat). These are the
// shape rules her proposal must meet before a card is shown. The server
// resolves ids and take numbers against the record (api/_lib/janet-design.js).
export const JANET_DESIGN_ACTIONS = ['new_brief', 'set_take_status']
export const JANET_TAKE_MOVES = ['star', 'unstar', 'candidate', 'approved', 'canon']
export const DEFAULT_JANET_PROVIDERS = ['openai', 'gemini', 'flux', 'seedream']
export const MAX_JANET_TAKES = 12
const MAX_PROMPT = 6000

export function normalizeDesignPayload(p) {
  if (!p || typeof p !== 'object') return { ok: false, reason: 'No design proposal was given.' }
  const action = JANET_DESIGN_ACTIONS.includes(p.action) ? p.action : null
  if (!action) return { ok: false, reason: 'Unknown design action.' }

  if (action === 'set_take_status') {
    const move = JANET_TAKE_MOVES.includes(p.move) ? p.move : null
    const number = Number(p.take_number)
    if (!p.brief_id) return { ok: false, reason: 'Which brief? I need the brief before I can mark a take.' }
    if (!Number.isInteger(number) || number < 1) return { ok: false, reason: 'Which take number?' }
    if (!move) return { ok: false, reason: 'Mark it as what — starred, a candidate, approved or canon?' }
    return { ok: true, payload: { action, brief_id: String(p.brief_id), take_number: number, move, canon_subject: str(p.canon_subject) } }
  }

  const prompt = str(p.prompt)
  if (!prompt) return { ok: false, reason: 'The brief has no prompt.' }
  if (prompt.length > MAX_PROMPT) return { ok: false, reason: 'The prompt is too long for the image models.' }
  const known = new Set(DESIGN_PROVIDERS.map(x => x.id))
  let providers = [...new Set((Array.isArray(p.providers) ? p.providers : []).filter(x => known.has(x)))]
  if (!providers.length) providers = [...DEFAULT_JANET_PROVIDERS]
  const variations = Math.min(3, Math.max(1, Math.round(Number(p.variations) || 1)))
  if (providers.length * variations > MAX_JANET_TAKES) return { ok: false, reason: `That is more than ${MAX_JANET_TAKES} takes at once.` }
  const numbers = [...new Set((Array.isArray(p.source_take_numbers) ? p.source_take_numbers : [])
    .map(Number).filter(n => Number.isInteger(n) && n >= 1))].sort((a, b) => a - b)
  if (numbers.length && !p.source_brief_id) return { ok: false, reason: 'Picks were named without the brief they come from.' }
  return {
    ok: true,
    payload: {
      action,
      property_id: p.property_id ? String(p.property_id) : null,
      project_id: p.project_id ? String(p.project_id) : null,
      subject_name: str(p.subject_name),
      title: str(p.title) || str(p.subject_name) || 'Design brief',
      brief_text: str(p.brief_text),
      direction: str(p.direction),
      prompt,
      providers,
      variations,
      source_brief_id: p.source_brief_id ? String(p.source_brief_id) : null,
      source_take_numbers: numbers,
    },
  }
}

// The one line the card shows above its button: what will run, nothing more.
export function describeDesignPayload(p) {
  if (!p) return ''
  if (p.action === 'set_take_status') {
    const verb = { star: 'Star', unstar: 'Unstar', candidate: 'Mark as a candidate:', approved: 'Approve', canon: 'Make canon:' }[p.move] || 'Mark'
    return `${verb} take #${p.take_number}${p.brief_title ? ` of “${p.brief_title}”` : ''}${p.move === 'canon' && p.canon_subject ? ` as ${p.canon_subject}` : ''}`
  }
  const takes = p.providers.length * p.variations
  const models = p.providers.map(providerBadge).join(' · ')
  const from = p.source_take_numbers?.length
    ? ` · from picks ${joinNumbers(p.source_take_numbers)}${p.source_brief_title ? ` of “${p.source_brief_title}”` : ''}`
    : ''
  return `${takes} take${takes === 1 ? '' : 's'} · ${models}${from}`
}

export function joinNumbers(ns = []) {
  const s = ns.map(n => `#${n}`)
  return s.length <= 1 ? s.join('') : `${s.slice(0, -1).join(', ')} and ${s[s.length - 1]}`
}

function str(v) {
  return typeof v === 'string' && v.trim() ? v.trim() : null
}

// The note a brief Janet started carries, so its lineage is on the record.
export function lineageNote(p) {
  const picks = p?.source_take_numbers?.length
    ? `Refined from picks ${joinNumbers(p.source_take_numbers)} of “${p.source_brief_title || 'an earlier brief'}”.`
    : null
  return [picks, p?.direction || null].filter(Boolean).join(' ') || null
}
